/*
 * THE HISTORICAL TOTAL ON EACH PAGE BOX (Chris, 2026-10-23: "I'm left wondering just how many visits historically each page in the slot boxes have had"; mockup design-prototypes/page-totals, style H).
 * A thin bar along the bottom edge of every box: how long it is says what share of ALL page views (in the Historical range chosen) this box's page had. The figure is the Pages tab's OVERALL column,
 * page views, one per page load, so a visitor who loads a page twice counts twice. A group's page already holds its members' views, and a member is never listed twice, so the pages add up to the total.
 * "All other pages" is everything that is not in a box. Pure, so it is tested without a browser.
 */
export interface TotalsPage {
  name: string;
  overallVisitors?: number;
}

export interface SlotTotal {
  views: number;
  /** 0..1 of all page views in the range */
  share: number;
}

export interface SlotTotals {
  total: number;
  /** by slot index, for the slots that hold a page */
  bySlot: Map<number, SlotTotal>;
  /** every page that is not in a box */
  other: SlotTotal;
}

const viewsOf = (page: TotalsPage | undefined): number => {
  const n = Number(page?.overallVisitors);

  return Number.isFinite(n) && n > 0 ? n : 0;
};

/** `slotNames[i]` is the page shown in box i ("" or undefined for an empty box) */
export const computeSlotTotals = (pages: TotalsPage[], slotNames: Array<string | undefined>): SlotTotals => {
  const byName = new Map<string, TotalsPage>();
  let total = 0;
  for (const page of pages || []) {
    if (!page || typeof page.name !== "string" || byName.has(page.name)) continue;
    byName.set(page.name, page);
    total += viewsOf(page);
  }
  const share = (views: number): number => (total > 0 ? views / total : 0);
  const bySlot = new Map<number, SlotTotal>();
  const used = new Set<string>();
  let inBoxes = 0;
  slotNames.forEach((name, slot) => {
    if (!name || used.has(name)) return;
    const views = viewsOf(byName.get(name));
    used.add(name);
    inBoxes += views;
    bySlot.set(slot, { views, share: share(views) });
  });
  const rest = Math.max(0, total - inBoxes);

  return { total, bySlot, other: { views: rest, share: share(rest) } };
};

/** "31%", and "<1%" for a share too small to round to 1 but above 0 */
export const percentText = (share: number): string => {
  if (!(share > 0)) return "0%";
  const pct = share * 100;

  return pct < 1 ? "<1%" : `${Math.round(pct)}%`;
};

/** the width of a bar's fill: its share of the box's width, but never invisible for a page that has views */
export const barFillWidth = (share: number, views: number, boxWidth: number): number => {
  if (!(views > 0) || !(boxWidth > 0)) return 0;

  return Math.min(boxWidth, Math.max(2, Math.round(share * boxWidth * 2) / 2));
};

/** the delayed tooltip on a bar: everything the box's label would have said */
export const slotTotalsText = (name: string, rangeName: string, t: SlotTotal): string =>
  `${name}${rangeName ? ` · ${rangeName}` : ""}\n${t.views.toLocaleString("en-US")} page view${t.views === 1 ? "" : "s"}\n${percentText(t.share)} of all page views`;
